import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Card, CardRemovalActivitiesAction, CardRemovalResult, CardService, Task, TasksService, TaskStatusEnum, taskTypeLabelKey } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';

export interface DeleteDealDialogData {
  card: Card;
}

/**
 * Deleting a deal (SPEC-2026-10-crm-activities-cadence RF-51, amended 2026-10-08). A deal with
 * open activities asks first what becomes of them — concluded (counted nowhere, like a conclusion
 * by a win) or deleted — and no option is preselected: the deletion waits for the choice. A deal
 * with none is a plain confirmation.
 *
 * The open activities are read only to show them: the API counts them again and refuses a deletion
 * that carries no choice, so a list read a moment too early can never leave one behind.
 *
 * Closes with the removal result once the deal is deleted, and with nothing otherwise. MatDialog
 * provides the shell; everything inside is Hyper markup, with Remix icons like the other kanban
 * dialogs.
 */
@Component({
  selector: 'app-delete-deal-dialog',
  templateUrl: './delete-deal-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DeleteDealDialogComponent extends AbstractComponent implements OnInit {

  /** How many open activities the dialog names; the others are counted. */
  static readonly SHOWN = 5;

  openActivities: Array<Task> = [];
  loadingActivities = true;
  /** The activities could not be read: the choice is offered anyway, and the API applies it only if there are any. */
  loadFailed = false;
  submitting = false;
  action: CardRemovalActivitiesAction | null = null;

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<DeleteDealDialogComponent, CardRemovalResult>,
    private readonly cardService: CardService,
    private readonly tasksService: TasksService,
    @Inject(MAT_DIALOG_DATA) public data: DeleteDealDialogData,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadActivities();
  }

  private loadActivities(): void {
    if (!this.data?.card?._id) {
      this.loadingActivities = false;
      return;
    }

    this.loadingActivities = true;
    this.loadFailed = false;
    this.tasksService.listByCard(this.data.card._id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingActivities = false)),
      )
      .subscribe({
        next: (tasks) => (this.openActivities = (tasks ?? []).filter((task) => task?.status !== TaskStatusEnum.DONE)),
        error: () => (this.loadFailed = true),
      });
  }

  /** The question is asked when the deal has open activities — or when it could not be told. */
  get asksAboutActivities(): boolean {
    return this.loadFailed || this.openActivities.length > 0;
  }

  get shownActivities(): Array<Task> {
    return this.openActivities.slice(0, DeleteDealDialogComponent.SHOWN);
  }

  get hiddenCount(): number {
    return Math.max(0, this.openActivities.length - DeleteDealDialogComponent.SHOWN);
  }

  get canConfirm(): boolean {
    return !this.loadingActivities && !this.submitting && (!this.asksAboutActivities || this.action !== null);
  }

  /** The activity's own title — unless it is the API's fallback (the type key itself), which reads as the type label. */
  title(task: Task): string {
    const name = task?.name?.trim();
    return name && name !== task?.type ? name : this.t(taskTypeLabelKey(task?.type));
  }

  typeLabel(task: Task): string {
    return taskTypeLabelKey(task?.type);
  }

  momentFormat(task: Task): string {
    return task?.untimed === true ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm';
  }

  choose(action: CardRemovalActivitiesAction): void {
    this.action = action;
  }

  confirm(): void {
    if (!this.canConfirm || !this.data?.card?._id) return;

    this.submitting = true;
    this.cardService.removeDeal(this.data.card._id, this.asksAboutActivities ? this.action ?? undefined : undefined)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.submitting = false)),
      )
      .subscribe({
        next: (result) => this.dialogRef.close(result ?? { concluded: 0, deleted: 0 }),
        error: (error) => {
          this.alertService.error(error?.error?.message ?? this.t('kanban.deleteDeal.error'));
          // An activity created after the dialog opened: the API asked for the choice — show it.
          if (error?.status === 400 && !this.asksAboutActivities) this.loadActivities();
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
